import { fetchServerEvaluatedTargetingConditions } from 'api/targeting'
import store from 'config/store'
import {
  getCampaignFromEmbedById, showUnreadCampaign
} from 'modules/campaigns'
import { renderAnnouncementCampaign, renderBotCampaign } from 'modules/campaigns/renderer'
import { setMatchedConversationRoutingRules } from 'modules/conversations/actions'
import { getBotSenderId } from 'modules/embed/utils'
import {
  evaluateCampaignTargetingConditions, evaluateRoutingTargetingConditions,
  evaluateWidgetDisplayTargetingConditions
} from 'modules/targeting/evaluation'
import { toggleMatchedWidgetTargeting } from 'modules/view/actions'
import { isEmpty, pathOr } from 'ramda'
import {
  MatchedRoutingRule, ServerTargetingEvaluationResults, TargetingEvaluationRequestContext
} from 'types/targeting'
import { error, log } from 'utils'

import { getIsOnline } from './availability'

export const evaluateWidgetTargeting = async (
  { forceShow = false, targetingResults = {} }:
    { forceShow: boolean, targetingResults?: Record<string, any> }
) => {
  const { skipWidgetTargeting } = store.getState().targeting
  const dispatchWidgetTargetingResult = (matchedHours: boolean, matchedTargeting: boolean) => {
    store.dispatch(toggleMatchedWidgetTargeting({
      matchedTargeting,
      matchedHours
    }))
  }

  const matchedHours = await getIsOnline()
  if (skipWidgetTargeting) {
    dispatchWidgetTargetingResult(matchedHours, false)
    return
  }

  if (forceShow) {
    dispatchWidgetTargetingResult(true, true)
  } else {
    evaluateWidgetDisplayTargetingConditions({
      evaluationMatchOutcome: targetingResults.evaluationMatchOutcome,
      evaluationResults: targetingResults.evaluationResults,
      onMatched: () => {
        dispatchWidgetTargetingResult(matchedHours, true)
      },
      onDidNotMatch: () => dispatchWidgetTargetingResult(matchedHours, false)
    })
  }
}

export const evalTargetingAndEnrollment = async (
  params: {
    evaluatedConditionsResults?: ServerTargetingEvaluationResults,
    onDidNotMatch?: () => void,
    unreadCampaignIds?: number[],
    skipCampaignTargeting?: boolean
  } = {}
) => {
  const {
    onDidNotMatch = () => { },
    unreadCampaignIds = [],
    evaluatedConditionsResults,
    skipCampaignTargeting = false
  } = params

  const state = store.getState()
  const { skipCampaigns, forceShowWidget } = state.targeting
  const { id: embedId } = state.embed
  const { campaignRefreshToken } = state.session

  const evaluateTargetingConditions = async (results: ServerTargetingEvaluationResults) => {
    const matchedRoutingRules: MatchedRoutingRule[] = await evaluateRoutingTargetingConditions(results.routingEvaluationResults)
    const shouldSkipCampaignTargeting = skipCampaignTargeting || skipCampaigns
    store.dispatch(setMatchedConversationRoutingRules(matchedRoutingRules))

    await evaluateWidgetTargeting({
      forceShow: forceShowWidget,
      targetingResults: results.widgetTargetingResults
    })

    if (shouldSkipCampaignTargeting) {
      return
    }

    const onCampaignMatch = async (matchedId: number, matchedAsyncCondition: boolean) => {
      const shouldRenderUnreadCampaign = unreadCampaignIds.includes(matchedId)
      if (shouldRenderUnreadCampaign) {
        return showUnreadCampaign(matchedId)
      }

      const matchingCampaign = getCampaignFromEmbedById(matchedId) || {}

      const isAnnouncementCampaign = matchingCampaign.type !== "BOT"

      if (isAnnouncementCampaign) {
        renderAnnouncementCampaign(matchedId, matchedAsyncCondition)
      } else {
        renderBotCampaign(matchedId, matchingCampaign.attributes.playbookId, getBotSenderId(), campaignRefreshToken, matchedAsyncCondition)
      }
    }

    await evaluateCampaignTargetingConditions({
      conditions: results.campaignTargetingResults,
      onMatched: onCampaignMatch,
      onDidNotMatch: onDidNotMatch
    })
  }

  try {
    if (skipCampaigns && forceShowWidget) {
      evaluateWidgetTargeting({ forceShow: true })
      return
    }

    // only evaluated if someone calls drift.page
    if (!evaluatedConditionsResults || isEmpty(evaluatedConditionsResults)) {
      const targetingEvaluationContext = getTargetingEvaluationContext()

      const { data } = await fetchServerEvaluatedTargetingConditions({
        embedId,
        ...targetingEvaluationContext,
        followingCampaignIds: unreadCampaignIds,
        excludedCampaignIds: []
      })

      if (!data) {
        log({
          data: ['Empty payload returned for campaign targeting'],
          type: 'warn'
        })
        return
      }

      evaluateTargetingConditions(data)
    } else {
      evaluateTargetingConditions(evaluatedConditionsResults)
    }

  } catch (err) {
    error(new Error(`Failed to evaluate campaign targeting - ${err.message}`), err)
  }
}

export const getTargetingEvaluationMetadata = () => {
  const { session, embed } = store.getState()
  const endUserId = pathOr(undefined, ['endUser', 'id'], session)
  const orgId = embed.orgId

  return {
    targetingContext: {
      endUserId,
      orgId
    },
    evaluationOptions: { ignoreUnknowns: false }
  }
}

export const getTargetingEvaluationContext = (): TargetingEvaluationRequestContext => {
  const state = store.getState()
  const { campaignRefreshToken, geo, context, assignedAgentId } = state.session
  const location = !isEmpty(geo) ? geo : undefined
  const pageUrl = pathOr('', ['page', 'href'], context)
  const conversations = pathOr({}, ['conversations', 'conversations'], state)
  const hasHadConversations = !isEmpty(conversations)
  const inboxId = pathOr(undefined, ['embed', 'configuration', 'inboxId'], state)

  return {
    sessionId: campaignRefreshToken,
    location,
    pageUrl,
    assignedAgentId,
    hasHadConversations,
    inboxId
  }
}
